/**
 * CustomerInfoTab — the work order's customer, edited where it is read (#2105).
 *
 * Work-order-level facts (the orderer party, the contact person, and the
 * work order's own contact phone/email) are changed inline on this card. The
 * registry record itself (name, address, business id) is still edited on the
 * person/company page (#833 B4) — the name links there.
 */

import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import EmailIcon from '@mui/icons-material/Email';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhoneIcon from '@mui/icons-material/Phone';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { getCustomerDisplayName } from '../../utils/customer';
import { CopyButton, UserAvatar } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { PartyPicker, type PartyRef } from '@/components/parties/PartyPicker';
import { useCompanyPersonLinks } from '@/hooks/useCompanies';
import { useUpdateBooking } from '@/hooks/useBookings';
import { useSetWorkOrderParty } from '@/hooks/useWorkOrderParties';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { useNotify } from '@/stores/notificationStore';
import { InlineTextEdit } from './InlineTextEdit';

interface CustomerInfoTabProps {
  workOrder: WorkOrderDetail;
  t: TFunction;
  /** Registry page of the orderer party; null when no orderer is set. */
  ordererPartyPath?: string | null;
}

export function CustomerInfoTab({
  workOrder,
  t,
  ordererPartyPath,
}: CustomerInfoTabProps) {
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateBooking = useUpdateBooking();
  const setWorkOrderParty = useSetWorkOrderParty();

  const currentOrderer = useMemo(
    () => workOrder.parties?.find((p) => p.role === 'orderer') ?? null,
    [workOrder.parties],
  );
  const currentPrimaryContactId = useMemo(
    () => workOrder.parties?.find((p) => p.role === 'primary_contact')?.personId ?? '',
    [workOrder.parties],
  );

  // "Change orderer" lives behind a 3-dot overflow menu so the sensitive, rare
  // action doesn't shout from the customer card header (#552).
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [isChangingOrderer, setIsChangingOrderer] = useState(false);
  const [pendingOrderer, setPendingOrderer] = useState<PartyRef | null>(null);

  const isCompanyOrderer = currentOrderer?.partyType === 'company';
  const { data: personLinksData, isLoading: isLoadingContacts } = useCompanyPersonLinks(
    isCompanyOrderer ? (currentOrderer?.companyId ?? undefined) : undefined,
  );
  const contactLinks = useMemo(
    () => (personLinksData?.data ?? []).filter((link) => link.validTo === null),
    [personLinksData],
  );

  const displayPhone = workOrder.contactPhone ?? workOrder.customer.phone;
  const displayEmail = workOrder.contactEmail ?? workOrder.customer.email;
  const hasOverriddenPhone =
    workOrder.contactPhone != null && workOrder.contactPhone !== workOrder.customer.phone;
  const hasOverriddenEmail =
    workOrder.contactEmail != null && workOrder.contactEmail !== workOrder.customer.email;

  const saveContact = async (
    data: { contactPhone: string | null } | { contactEmail: string | null },
  ) => {
    try {
      await updateBooking.mutateAsync({ id: workOrder.id, data });
      notify.success(t('editSuccess'), t('editSuccessDesc'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('editFailed') }));
    }
  };

  const handleContactPersonChange = async (personId: string) => {
    const selected = contactLinks.find((link) => link.personId === personId);
    try {
      await updateBooking.mutateAsync({
        id: workOrder.id,
        data: {
          primaryContactPersonId: personId || null,
          // Adopt the chosen person's details as the work order's contact, but
          // never blank an existing override with a person who has none.
          ...(selected?.phone ? { contactPhone: selected.phone } : {}),
          ...(selected?.email ? { contactEmail: selected.email } : {}),
        },
      });
      notify.success(t('editSuccess'), t('editSuccessDesc'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('editFailed') }));
    }
  };

  const handleSelectOrderer = (party: PartyRef) => {
    const isSame =
      currentOrderer
      && party.partyType === currentOrderer.partyType
      && (party.partyType === 'person'
        ? party.personId === currentOrderer.personId
        : party.companyId === currentOrderer.companyId);
    if (isSame) {
      setIsChangingOrderer(false);
      return;
    }
    setPendingOrderer(party);
  };

  const handleConfirmOrdererChange = async () => {
    if (!pendingOrderer) return;
    try {
      await setWorkOrderParty.mutateAsync({
        workOrderId: workOrder.id,
        role: 'orderer',
        data:
          pendingOrderer.partyType === 'person'
            ? { partyType: 'person', personId: pendingOrderer.personId ?? undefined }
            : { partyType: 'company', companyId: pendingOrderer.companyId ?? undefined },
      });
      notify.success(t('editCustomerChanged'), t('editCustomerChangeSuccess'));
      setIsChangingOrderer(false);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('editFailed') }));
    } finally {
      setPendingOrderer(null);
    }
  };

  const customerName = getCustomerDisplayName(workOrder.customer);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <UserAvatar
          firstName={workOrder.customer.firstName ?? ''}
          lastName={workOrder.customer.lastName ?? ''}
          size={48}
          variant="rounded"
          colorScheme={workOrder.customer.customerType === 'business' ? 'business' : 'default'}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {/* describeChild keeps the customer name as the link's accessible
                name — a plain Tooltip would overwrite it with its own title. */}
            {ordererPartyPath ? (
              <Tooltip title={t('detail.editCustomer')} describeChild>
                <Link
                  component={RouterLink}
                  to={ordererPartyPath}
                  variant="h6"
                  underline="hover"
                  noWrap
                  sx={{ display: 'block', minWidth: 0 }}
                >
                  {customerName}
                </Link>
              </Tooltip>
            ) : (
              <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
                {customerName}
              </Typography>
            )}
            <CopyButton
              value={customerName}
              label={tc('copyField', { field: customerName })}
            />
          </Box>
          {workOrder.customer.customerType === 'business' && workOrder.contactPerson && (
            <Typography variant="body2" color="text.secondary">
              {t('detail.contactPerson')}: {workOrder.contactPerson.firstName}{' '}
              {workOrder.contactPerson.lastName}
            </Typography>
          )}
        </Box>
        {!isChangingOrderer && (
          <>
            <Tooltip title={t('detail.customerActions')}>
              <IconButton
                size="small"
                onClick={(event) => { setActionsAnchor(event.currentTarget); }}
                aria-label={t('detail.customerActions')}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={actionsAnchor}
              open={Boolean(actionsAnchor)}
              onClose={() => { setActionsAnchor(null); }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
              <MenuItem
                onClick={() => {
                  setActionsAnchor(null);
                  setIsChangingOrderer(true);
                }}
              >
                <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
                {t('editChangeCustomer')}
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {isChangingOrderer && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <PartyPicker onSelect={handleSelectOrderer} autoFocus />
          <Button size="small" onClick={() => { setIsChangingOrderer(false); }} sx={{ alignSelf: 'flex-start' }}>
            {tc('cancel')}
          </Button>
        </Box>
      )}

      {/* Contact person for company orderers — from the company's person links */}
      {isCompanyOrderer && (
        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('create.selectContactPerson')}</InputLabel>
          <Select
            value={currentPrimaryContactId}
            label={t('create.selectContactPerson')}
            disabled={isLoadingContacts || updateBooking.isPending}
            onChange={(event) => { void handleContactPersonChange(event.target.value); }}
          >
            <MenuItem value="">
              <em>{tc('none')}</em>
            </MenuItem>
            {contactLinks.map((link) => (
              <MenuItem key={link.id} value={link.personId}>
                {[link.firstName, link.lastName].filter(Boolean).join(' ')}
                {link.isPrimary ? ' *' : ''}
                {link.role ? ` — ${link.role}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <List dense disablePadding>
        <ListItem disablePadding sx={{ py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <PhoneIcon fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            disableTypography
            primary={
              <InlineTextEdit
                label={t('detail.contactPhone')}
                value={displayPhone}
                type="tel"
                saving={updateBooking.isPending}
                onSave={(value) => { void saveContact({ contactPhone: value || null }); }}
                copyable
                {...(hasOverriddenPhone
                  ? {
                      adornment: (
                        <Tooltip title={t('detail.workOrderContact')}>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'info.main' }} />
                        </Tooltip>
                      ),
                    }
                  : {})}
              />
            }
          />
        </ListItem>
        <ListItem disablePadding sx={{ py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <EmailIcon fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            disableTypography
            primary={
              <InlineTextEdit
                label={t('detail.contactEmail')}
                value={displayEmail}
                type="email"
                saving={updateBooking.isPending}
                onSave={(value) => { void saveContact({ contactEmail: value || null }); }}
                copyable
                {...(hasOverriddenEmail
                  ? {
                      adornment: (
                        <Tooltip title={t('detail.workOrderContact')}>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'info.main' }} />
                        </Tooltip>
                      ),
                    }
                  : {})}
              />
            }
          />
        </ListItem>
      </List>

      <ConfirmDialog
        open={Boolean(pendingOrderer)}
        title={t('editConfirmChangeCustomer')}
        message={t('editConfirmChangeCustomerDesc', { customerName: pendingOrderer?.name ?? '' })}
        confirmText={tc('confirm')}
        cancelText={tc('cancel')}
        onConfirm={() => { void handleConfirmOrdererChange(); }}
        onCancel={() => { setPendingOrderer(null); }}
      />
    </>
  );
}
